// SchematicDoc (doc 07 §5) and the schematic API contract (doc 08 §6).
import { z } from 'zod';
import { portSchema, type FLUIDS } from './assets';
import { objectIdSchema } from './common';
import { dashboardSummarySchema, scopeBindingSchema, sharingSchema, thresholdSchema } from './dashboards';
import { bindingSchema } from './data';

export const SCHEMATIC_SCHEMA_VERSION = 1;

/** Process line fluids (doc 07 §2.2): the equipment port fluids plus instrument lines. */
export const LINE_FLUIDS = ['multiphase', 'oil', 'gas', 'water', 'relief', 'chemical', 'utility', 'instrument'] as const;
export type LineFluid = (typeof LINE_FLUIDS)[number];
export const LINE_STYLES: Record<LineFluid, { color: string; width: number; dash: string | null }> = {
  multiphase: { color: '#6B4FA0', width: 3, dash: null },
  oil: { color: '#7A4A1E', width: 3, dash: null },
  gas: { color: '#D4A017', width: 3, dash: null },
  water: { color: '#1F6FB2', width: 3, dash: null },
  relief: { color: '#C2410C', width: 2, dash: '8 5' },
  instrument: { color: '#55616D', width: 1.5, dash: '2 4' },
  chemical: { color: '#0E8F7E', width: 2, dash: null },
  utility: { color: '#8C99A5', width: 2, dash: null },
};

/** A line may join two ports whose fluids match the line or are `any`; instrument lines are free. */
export function portAccepts(portFluid: (typeof FLUIDS)[number], line: LineFluid): boolean {
  return line === 'instrument' || portFluid === 'any' || portFluid === line;
}

const nodeId = z.string().regex(/^[\w-]{1,32}$/);
const coord = z.number().min(-20_000).max(20_000);
const base = { id: nodeId, x: coord, y: coord, locked: z.boolean().optional() };
const tagBase = {
  ...base,
  /** Equipment node the tag belongs to: it moves with it. */
  attachTo: nodeId.nullable().optional(),
  binding: bindingSchema.nullable(),
  label: z.string().max(80).default(''),
};

export const schematicNodeSchema = z.discriminatedUnion('kind', [
  z.object({
    ...base,
    kind: z.literal('equipment'),
    typeKey: z.string().max(48),
    w: z.number().min(10).max(2000),
    h: z.number().min(10).max(2000),
    rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
    flipH: z.boolean().default(false),
    equipmentInstanceId: objectIdSchema.nullable().default(null),
    label: z.string().max(80).default(''),
  }),
  z.object({
    ...tagBase,
    kind: z.literal('valueTag'),
    decimals: z.number().int().min(0).max(8).nullable().default(null),
    displayUnitId: z.string().max(64).nullable().default(null),
    thresholds: z.array(thresholdSchema).max(8).default([]),
  }),
  /** Coloured dot and state label of a boolean signal. */
  z.object({ ...tagBase, kind: z.literal('status') }),
  /** Valve symbol, filled when open. */
  z.object({ ...tagBase, kind: z.literal('valve') }),
  /** Vertical bar for a 0–100 % level. */
  z.object({ ...tagBase, kind: z.literal('level'), w: z.number().min(8).max(200).default(18), h: z.number().min(20).max(600).default(80) }),
  z.object({ ...base, kind: z.literal('text'), text: z.string().max(500), size: z.number().min(8).max(72).default(14) }),
  /** Labelled area such as "Hazardous zone". */
  z.object({ ...base, kind: z.literal('area'), w: z.number().min(20).max(8000), h: z.number().min(20).max(8000), label: z.string().max(80).default('') }),
]);
export type SchematicNode = z.infer<typeof schematicNodeSchema>;
export type SchematicNodeKind = SchematicNode['kind'];
export type EquipmentNode = Extract<SchematicNode, { kind: 'equipment' }>;
export type TagNode = Extract<SchematicNode, { kind: 'valueTag' | 'status' | 'valve' | 'level' }>;
export const isTagNode = (n: SchematicNode): n is TagNode => n.kind === 'valueTag' || n.kind === 'status' || n.kind === 'valve' || n.kind === 'level';

export const schematicEdgeSchema = z.object({
  id: nodeId,
  source: nodeId,
  sourcePort: z.string().max(32).nullable().default(null),
  target: nodeId,
  targetPort: z.string().max(32).nullable().default(null),
  fluid: z.enum(LINE_FLUIDS).default('multiphase'),
  routing: z.enum(['step', 'straight', 'smooth']).default('step'),
  waypoints: z.array(z.tuple([coord, coord])).max(20).default([]),
  /** When its value is above the threshold the line is drawn flowing. */
  flowSignal: bindingSchema.nullable().default(null),
  flowThreshold: z.number().default(0),
  label: z.string().max(60).default(''),
});
export type SchematicEdge = z.infer<typeof schematicEdgeSchema>;

export const schematicDocSchema = z.object({
  schemaVersion: z.literal(SCHEMATIC_SCHEMA_VERSION).default(SCHEMATIC_SCHEMA_VERSION),
  variables: z.array(z.object({ name: z.string().max(24), type: z.preprocess((v) => (v === 'wellsite' ? 'well' : v), z.enum(['well'])) })).max(4).default([{ name: 'well', type: 'well' }]),
  canvas: z
    .object({
      background: z.enum(['light', 'dark', 'blueprint']).default('light'),
      /** Snap step in px; 0 = free. */
      grid: z.number().int().min(0).max(100).default(10),
    })
    .default({}),
  nodes: z.array(schematicNodeSchema).max(400).default([]),
  edges: z.array(schematicEdgeSchema).max(400).default([]),
});
export type SchematicDoc = z.infer<typeof schematicDocSchema>;
export const emptySchematicDoc = (): SchematicDoc => schematicDocSchema.parse({});

/** Geometry of the equipment types a schematic uses, sent with it so every viewer can draw it. */
export const schematicTypeSchema = z.object({
  key: z.string(),
  name: z.string(),
  size: z.object({ w: z.number(), h: z.number() }),
  ports: z.array(portSchema),
  /** Sanitized SVG of a custom stencil; null for the built-in symbols. */
  symbolSvg: z.string().nullable(),
});
export type SchematicType = z.infer<typeof schematicTypeSchema>;

export const schematicSummarySchema = dashboardSummarySchema;
export type SchematicSummary = z.infer<typeof schematicSummarySchema>;
export const schematicSchema = schematicSummarySchema.extend({
  doc: schematicDocSchema,
  types: z.array(schematicTypeSchema),
  sharing: sharingSchema.nullable(),
});
export type Schematic = z.infer<typeof schematicSchema>;

export const createSchematicRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
  fromTemplateId: objectIdSchema.optional(),
  bind: scopeBindingSchema.optional(),
});
export const saveSchematicRequestSchema = z.object({
  doc: schematicDocSchema,
  /** The version the edit was based on: a mismatch answers 409. */
  version: z.number().int().min(0),
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  note: z.string().max(200).optional(),
});
